import type { CSSProperties, MouseEvent, PointerEvent, ReactNode } from 'react'
import { useEffect, useRef, useState } from 'react'
import { useCanHover } from '../app/useViewport'
import { Face } from '../components/primitives'
import { radius } from '../components/primitives/shape'
import { MessageToolbar, Reactions } from '../components/Reactions'
import type { Agent, EventDetails, Message, ReactionName, ReplyPreview } from '../api/types'
import { onItOf } from './activity'
import { MessageSheet } from './MessageSheet'
import { OnItLine } from './OnIt'
import { newTabFor, openLink } from './openLink'
import {
  answerOf, buildStream, buildThread, clockOf, countOfReplies, dayLabel, deliveryLine, displayName, isLong, latestRunStep, parseBody,
  roomMessagesOf, runLine, sinceLabel, splitBody, tagsReader, threadRootOf, threadsOf, visitingName, whereAgentRuns, type RunStep,
} from './format'

/**
 * A tag says whether it points at anybody. An agent and a person share the one
 * accent, `@here` is every agent in the room at once, and a handle matching
 * neither is a typo that should not wear the colours of a live mention.
 *
 * **`@here` is not a bigger `@agent`, it is a different act.** Naming one agent
 * routes a task; `@here` interrupts everybody's next turn, and it is reserved
 * for the handful of things the whole room has to act on — a freeze, a required
 * rebase, a migration. It read as one more blue word among the others, which is
 * how it gets used for announcements nobody needed. Same accent, no new colour:
 * it simply takes up more of it.
 */
export type MentionKind = 'agent' | 'human' | 'everyone' | 'unknown'

const NAMED_MENTION: CSSProperties = {
  color: 'var(--mention-agent)',
  fontWeight: 700,
  borderBottom: '1px solid var(--mention-agent-edge)',
}

const MENTION_STYLES: Record<MentionKind, CSSProperties> = {
  agent: NAMED_MENTION,
  human: NAMED_MENTION,
  everyone: {
    color: 'var(--accent-bright)',
    fontWeight: 800,
    background: 'var(--accent-wash)',
    border: '1px solid var(--accent-edge)',
    borderRadius: radius.xs,
    padding: '1px 7px 2px',
    // The pill must not stretch the line it sits in: a body set at 1.62 leading
    // has room for the padding, and box-decoration-break keeps the shape whole
    // if it ever wraps.
    boxDecorationBreak: 'clone',
    WebkitBoxDecorationBreak: 'clone',
    whiteSpace: 'nowrap',
  },
  unknown: {
    color: 'var(--mention-unknown)',
    fontWeight: 500,
  },
}
const CODE_STYLE = {
  fontFamily: 'var(--font-machine)',
  fontSize: '.88em',
  color: '#fff',
}

/** Marks the selected row so j/k can scroll it into view without a ref per message. */
export const SELECTED_ATTRIBUTE = 'data-selected'

function Body({ body, serif, mentionKind, oneLine }: {
  body: string
  serif?: boolean
  /** Who a handle turns out to be. Defaults to unknown, which dims it. */
  mentionKind?: (handle: string) => MentionKind
  /** The headline standing in for the body it summarises: one line, clipped
   * with an ellipsis rather than wrapped. */
  oneLine?: boolean
}) {
  return (
    <div
      className="sp-said"
      style={{
        fontFamily: serif ? 'var(--font-human)' : 'var(--font-interface)',
        fontSize: 'var(--size-body)',
        lineHeight: 'var(--line-body)',
        color: serif ? 'rgba(255,255,255,.94)' : 'rgba(237,237,237,.85)',
        // Agents post stack traces, so losing their shape loses the message,
        // except for a headline, which is a single sentence shown one line at
        // a time on purpose.
        whiteSpace: oneLine ? 'nowrap' : 'pre-wrap',
        overflow: oneLine ? 'hidden' : undefined,
        textOverflow: oneLine ? 'ellipsis' : undefined,
        textWrap: 'pretty',
      }}
    >
      {parseBody(body).map((part, index) => (
        <span
          key={index}
          style={
            part.kind === 'mention'
              ? MENTION_STYLES[mentionKind?.(part.handle ?? '') ?? 'unknown']
              : part.kind === 'code'
                ? CODE_STYLE
                : undefined
          }
        >
          {part.text}
        </span>
      ))}
    </div>
  )
}

/** Anything the room reports rather than says: a merge, a push, a status
 * update. Structure, not chat: no avatar, no bubble, and exactly one line.
 *
 * One line is the rule, not the typical case. An event carries a lead line from
 * whatever it is reporting, and left to wrap, six of them arriving together
 * become a wall taller than the conversation they interrupt. The title is the
 * fact and holds its width; the meta is the flavour and gives its up first. */
const ONE_LINE = {
  minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
} as const

/**
 * Two branches heading for one file.
 *
 * Deliberately the one event that breaks the hairline rule above. Everything
 * else the room reports is state a reader may note and move on from; this asks
 * somebody to make a decision, and it cannot be a grey line between two
 * messages. It keeps the event's shape — no bubble, no sender — and takes the
 * accent, the faces of everyone involved, and the paths in mono, because "who"
 * and "which file" are the entire content.
 *
 * **It says what is true, which is that nothing is broken.** Both agents are in
 * their own worktree and both will keep working; the cost arrives at the merge
 * and is paid by whoever is second. A row that shouted about a clash happening
 * right now would be visibly wrong to the agent reading it, and the next one
 * would be ignored.
 */
function OverlapRow({ message, overlap, dot, timeOf }: {
  message: Message
  overlap: NonNullable<EventDetails['overlap']>
  dot: ReactNode
  timeOf: (when: string) => string
}) {
  return (
    <div
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 12, margin: '8px 0',
        padding: '11px 13px',
        background: 'var(--accent-wash)',
        border: '1px solid var(--accent-edge)',
        borderRadius: radius.md,
      }}
    >
      <span style={{ display: 'flex', flex: 'none', paddingTop: 1 }}>
        {overlap.agents.map((agent, index) => (
          <span key={agent} style={{ marginLeft: index ? -7 : 0, display: 'flex' }}>
            <Face name={agent} avatarKey={null} size={20} />
          </span>
        ))}
      </span>
      <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ font: '700 9.5px/1 var(--font-interface)', letterSpacing: '.18em', textTransform: 'uppercase', color: 'var(--accent)' }}>
          Same file, two branches
        </span>
        <span style={{ font: '500 12px/1.55 var(--font-machine)', color: 'var(--text)', overflowWrap: 'anywhere' }}>
          {overlap.agents.join(' and ')} are both changing{' '}
          {overlap.paths.map((path, index) => (
            <span key={path}>
              {index > 0 && ', '}
              <span style={{ color: 'var(--accent-bright)' }}>{path}</span>
            </span>
          ))}
        </span>
        <span style={{ font: '400 11.5px/1.5 var(--font-human)', fontStyle: 'italic', color: 'var(--text-dim)' }}>
          Nothing is broken — separate worktrees. Whoever merges second
          inherits it, so agree now who owns the file.
        </span>
      </span>
      <span style={{ flex: 'none', font: '400 11.5px/1 var(--font-machine)', color: 'var(--text-faint)', paddingTop: 3 }}>
        {timeOf(message.posted_at)}
      </span>
      {dot}
    </div>
  )
}

function DiffCounts({ event }: { event: EventDetails }) {
  return (
    <>
      <span style={{ color: 'var(--additions)' }}>{event.additions}</span>{' '}
      <span style={{ color: 'var(--deletions)' }}>{event.deletions}</span>
    </>
  )
}

/** How wide the stream and the composer read, centred in their panel. */
export const READING_WIDTH = 860

/** How close to the bottom still counts as "watching the live edge", in pixels, in the room and in a thread. */
export const PINNED_WITHIN = 60

/**
 * 22px of gutter each side is a tenth of a 360px screen spent on nothing, so a
 * phone gives the stream the width. Wider, the gutters grow to centre a column
 * of reading width.
 */
export function streamPadding(compact: boolean): string {
  return compact ? '0 14px 8px' : `0 max(22px, calc((100% - ${READING_WIDTH}px) / 2)) 8px`
}

/** The face's width and the gap after it: where every line of text in the stream starts. */
const FACE = 30
const TEXT_COLUMN = FACE + 10

const TIME_STYLE: CSSProperties = { font: '400 10.5px var(--font-machine)', color: 'var(--text-faint)' }
const PERSON_NAME_STYLE: CSSProperties = { font: '700 13px var(--font-interface)' }
const HANDLE_NAME_STYLE: CSSProperties = { font: '600 12.5px var(--font-machine)' }

/** An agent as a person reads it: the nickname with the handle faint after it, to tell apart and tag by, or the handle alone. */
export function AgentName({ handle, nickname }: { handle: string; nickname: string | null }) {
  return (
    <>
      {displayName(handle, nickname)}
      {/* The space keeps the two apart in an accessible name, which a margin does not. */}
      {nickname && ' '}
      {nickname && (
        <span style={{ marginLeft: 3, fontFamily: 'var(--font-machine)', fontSize: 11, fontWeight: 400, color: 'var(--text-faint)' }}>
          {handle}
        </span>
      )}
    </>
  )
}

function EventRow({ message, dot, timeOf }: { message: Message; dot: ReactNode; timeOf: (when: string) => string }) {
  const event = message.event
  if (!event) return null
  if (event.overlap) return <OverlapRow message={message} overlap={event.overlap} dot={dot} timeOf={timeOf} />
  const diff = event.additions || event.deletions
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 14, padding: `7px 0 7px ${TEXT_COLUMN}px`,
        flexWrap: 'nowrap', font: '500 11.5px var(--font-machine)',
      }}
    >
      <span style={{ ...ONE_LINE, flex: '0 1 auto', color: 'var(--text-dim)' }}>
        {event.title}
      </span>
      {event.meta && (
        // Shrinks a hundred times more readily than the title, which is what
        // makes "the meta gives its width up first" true at every width rather
        // than only at the one this was looked at.
        <span style={{ ...ONE_LINE, flex: '0 100 auto', color: 'var(--text-faint)' }}>
          {event.meta}
        </span>
      )}
      {diff && (
        <span style={{ flexShrink: 0 }}>
          <DiffCounts event={event} />
        </span>
      )}
      <span style={{ flex: '1 0 20px', height: 1, background: 'var(--border)' }} />
      <span style={{ flexShrink: 0, color: 'var(--text-faint)' }}>
        {timeOf(message.posted_at)}
      </span>
      {dot}
    </div>
  )
}

/**
 * What a reply answers, above its name, when the room's page no longer holds
 * the start of its thread. Clicking it goes to that message, when the page
 * holds it; otherwise it is only text.
 */
function ReplyLine({ preview, nickname, onClick }: {
  preview: ReplyPreview
  /** The roster's, since a preview carries the handle only. */
  nickname: string | null | undefined
  onClick?: () => void
}) {
  const style: CSSProperties = {
    display: 'flex', alignItems: 'baseline', gap: 5, maxWidth: '100%',
    padding: `0 0 2px ${TEXT_COLUMN}px`, font: '400 11.5px var(--font-interface)', color: 'var(--text-meta)',
  }
  const said = (
    <>
      <span style={{ flex: 'none' }}>↪ replying to</span>{' '}
      <span style={{ flex: 'none', fontWeight: 600, color: 'var(--text-dim)' }}>{displayName(preview.sender, nickname)}</span>{' '}
      <span style={ONE_LINE}>{preview.headline}</span>
    </>
  )
  return onClick
    ? <button type="button" onClick={onClick} style={{ ...style, cursor: 'pointer' }}>{said}</button>
    : <div style={style}>{said}</div>
}

/** Above a reply also sent to the room, as in Slack: the thread it answers, which it opens. */
function ThreadLine({ headline, onClick }: { headline: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'baseline', gap: 5, maxWidth: '100%', cursor: 'pointer',
        padding: `0 0 2px ${TEXT_COLUMN}px`, font: '400 11.5px var(--font-interface)', color: 'var(--text-meta)',
      }}
    >
      <span style={{ flex: 'none' }}>replied to a thread:</span>{' '}
      <span style={{ ...ONE_LINE, color: 'var(--text-dim)' }}>{headline}</span>
    </button>
  )
}

/**
 * After the time of a message you have not read. Seeing it fades the dot
 * rather than taking it away, so the row does not move under the reader.
 */
function UnreadDot({ unread }: { unread: boolean }) {
  return (
    <span
      role="img"
      aria-label="Unread"
      aria-hidden={!unread}
      style={{
        flex: 'none', alignSelf: 'center', width: 7, height: 7, borderRadius: '50%', background: 'var(--accent)',
        opacity: unread ? 1 : 0, transform: unread ? 'none' : 'scale(.6)',
        transition: 'opacity .7s ease-out, transform .7s ease-out',
      }}
    />
  )
}

/** Slack's row under a first message with replies: the latest repliers, how many, how many unread, and when the last came. */
function ThreadRow({ replies, unread, onOpen }: { replies: Message[]; unread: number; onOpen: () => void }) {
  const last = replies[replies.length - 1].posted_at
  const faces = [...new Set(replies.map((reply) => reply.sender).reverse())].slice(0, 3)
  return (
    <button
      type="button"
      onClick={onOpen}
      style={{
        display: 'flex', alignItems: 'center', gap: 8, minHeight: 32, margin: `2px 0 0 ${TEXT_COLUMN - 6}px`,
        padding: '0 6px', borderRadius: radius.sm, cursor: 'pointer',
      }}
    >
      <span style={{ display: 'flex' }}>
        {faces.map((name, index) => (
          <span key={name} style={{ display: 'flex', marginLeft: index ? -6 : 0 }}>
            <Face name={name} size={22} />
          </span>
        ))}
      </span>
      <span style={{ font: '700 12.5px var(--font-interface)', color: 'var(--accent)' }}>{countOfReplies(replies.length)}</span>{' '}
      {unread > 0 && <><span style={{ font: '700 12px var(--font-interface)', color: 'var(--accent)' }}>{unread} new</span>{' '}</>}
      <span style={{ font: '400 12px var(--font-interface)', color: 'var(--text-meta)' }}>
        {dayLabel(last)} at {clockOf(last)}
      </span>
    </button>
  )
}

/** A question's answers. A click holds them all, the clicked one marked, until
 * its reply is posted; once a person replied they stay held, the chosen one
 * filled if the reply chose one, the rest dim. */
function Answers({ options, answered, onAnswer }: {
  options: string[]
  answered: ReturnType<typeof answerOf>
  onAnswer: (answer: string) => Promise<unknown>
}) {
  // An index, not the text: two answers may read the same.
  const [sending, setSending] = useState<number | null>(null)
  const [failure, setFailure] = useState<string | null>(null)
  const held = sending !== null || answered.replied
  const marked = sending ?? (answered.chosen === null ? null : options.indexOf(answered.chosen))

  async function answer(index: number) {
    setSending(index)
    setFailure(null)
    await onAnswer(options[index]).catch((error) => setFailure(error instanceof Error ? error.message : 'not sent'))
    setSending(null)
  }

  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {options.map((option, index) => {
          const picked = index === marked
          return (
            <button
              key={index}
              type="button"
              disabled={held}
              aria-pressed={held ? picked : undefined}
              onClick={() => void answer(index)}
              style={{
                padding: '5px 12px', borderRadius: radius.action, font: '500 12px var(--font-interface)',
                border: `1px solid ${picked || index === 0 ? 'var(--accent)' : 'var(--border-strong)'}`,
                backgroundColor: picked ? 'var(--accent)' : 'transparent',
                color: picked ? 'var(--ground)' : index === 0 ? 'var(--accent)' : 'var(--text-soft)',
                opacity: held && !picked ? 0.45 : 1,
                cursor: held ? 'default' : 'pointer',
                overflowWrap: 'anywhere',
              }}
            >
              {option}
            </button>
          )
        })}
      </div>
      {failure && <p role="alert" style={{ margin: '6px 0 0', fontSize: 12, color: 'var(--deletions)' }}>{failure}</p>}
    </div>
  )
}

/**
 * One message, in Slack's shape: the face, a line with the name and the time,
 * the text under it. `continued` draws it under the message above, from the
 * same sender, with neither.
 */
function MessageRow({
  message, agent, channelId, continued, roomAgents, mentionKind, myName, onOpenProfile, onReact, answering,
  onOpenThread, place, label, actions, dot, timeOf,
}: {
  message: Message
  agent: Agent | undefined
  channelId: string
  continued: boolean
  /** The room's own agents by name, visitors left out: who reads it as context. */
  roomAgents: string[]
  mentionKind: (handle: string) => MentionKind
  myName: string
  onOpenProfile?: (name: string) => void
  onReact?: (message: Message, reaction: ReactionName, held: boolean) => void
  /** In the room, above a reply shown there: the thread or the message it answers. */
  answering?: ReactNode
  /** In a conversation, what a thread reply's label opens. */
  onOpenThread?: () => void
  /** In a conversation, the room it was said in, or null for a private message. */
  place?: string | null
  /** In place of the place line, when it is not undefined. */
  label?: ReactNode
  actions?: ReactNode
  /** The unread dot, after the time, or in the gutter of a row drawn under the one before. */
  dot: ReactNode
  timeOf: (when: string) => string
}) {
  const [expanded, setExpanded] = useState(false)
  const human = message.sender_type === 'human'
  const openProfile = onOpenProfile && (() => onOpenProfile(message.sender))
  const nickname = message.sender_nickname
  const nameStyle = human || nickname ? PERSON_NAME_STYLE : HANDLE_NAME_STYLE
  const name = <AgentName handle={message.sender} nickname={nickname} />
  // The server already wrote the one-line summary; showing it in place of the
  // body is the whole feature. `headline` is empty from an older server, and
  // it equals the body outright for the roughly one message in five that is
  // already a single short line. Both cases fall through to the paragraph
  // split below, so a control that would do nothing never renders. A person's
  // message always shows whole.
  const headlineOrBody = message.headline || message.body
  const hasHeadline = !human && message.body.trim() !== headlineOrBody.trim()
  // An opening paragraph is written for a person and always shows; anything
  // after the first blank line is detail for agents and waits behind the
  // button. Without that split, fall back to clamping a long body. Only
  // reached when there is no headline to collapse to.
  const { summary, detail } = splitBody(message.body)
  const hasDetail = !human && !hasHeadline && detail.length > 0
  const long = !human && (hasHeadline || hasDetail || isLong(message))
  const runs = human ? null : whereAgentRuns(agent, channelId)

  return (
    <div style={{ padding: continued ? '1px 0' : '8px 0 1px' }}>
      {answering}
      <div style={{ display: 'flex', gap: TEXT_COLUMN - FACE }}>
        <div style={{ width: FACE, flex: 'none' }}>
          {/* `avatarKey` is undefined rather than null when the sender is not
              in the roster, which sends Face to the face context, where a
              person's own face is. */}
          {!continued && (
            <Face
              name={message.sender}
              size={FACE}
              avatarKey={agent?.avatar_key}
              online={agent?.online ?? true}
              onClick={openProfile}
            />
          )}
          {continued && dot && <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 7 }}>{dot}</div>}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          {!continued && (label !== undefined ? label : place && (onOpenThread && message.reply_to ? (
            <button
              type="button"
              onClick={onOpenThread}
              style={{ padding: 0, cursor: 'pointer', font: '600 11.5px var(--font-interface)', color: 'var(--text-meta)' }}
            >
              in a thread in #{place}
            </button>
          ) : (
            <div style={{ font: '600 11.5px var(--font-interface)', color: 'var(--text-meta)' }}># in {place}</div>
          )))}
          {!continued && (
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 1, minWidth: 0 }}>
              {!openProfile ? (
                <span style={{ ...ONE_LINE, ...nameStyle }}>{name}</span>
              ) : (
                <button
                  type="button"
                  onClick={openProfile}
                  className="sp-tap"
                  title={message.sender}
                  style={{
                    ...nameStyle,
                    cursor: 'pointer', padding: 0, minWidth: 0, maxWidth: '100%',
                  }}
                >
                  {/* The clipping goes on the span, never on the button. An agent
                      is named after its branch, so `claude/balena-fleet-integration`
                      is an ordinary name with no space to wrap at, and it needs an
                      ellipsis, but `overflow: hidden` on the button would also cut
                      off the hit area `.sp-tap` draws outside it. */}
                  <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {name}
                  </span>
                </button>
              )}
              {message.visiting_from && (
                // Where it lives, as a person from another organisation is
                // marked in a shared channel: beside the name, in the meta ink.
                // It keeps its width and the name gives way, since a room's
                // name is short and says more on a phone than the handle does.
                <span
                  title={message.visiting_from.repository_key}
                  style={{ flex: 'none', whiteSpace: 'nowrap', font: '400 11px var(--font-interface)', color: 'var(--text-meta)' }}
                >
                  from {visitingName(message.visiting_from)}
                </span>
              )}
              {runs && (
                <span
                  style={{
                    flex: 'none', alignSelf: 'center', padding: '3px 5px', borderRadius: radius.xs,
                    font: '600 9.5px/1 var(--font-machine)', letterSpacing: '.05em',
                    background: runs.fromRoom ? 'var(--accent-wash)' : 'var(--panel-strong)',
                    color: runs.fromRoom ? 'var(--accent)' : 'var(--text-meta)',
                  }}
                >
                  {runs.text}
                </span>
              )}
              <span style={{ ...TIME_STYLE, flex: 'none' }}>{timeOf(message.posted_at)}</span>
              {message.edited_at && <span style={{ ...TIME_STYLE, flex: 'none' }}>edited</span>}
              {dot}
            </div>
          )}

          <div
            style={
              long && !hasHeadline && !hasDetail && !expanded
                ? { display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden' }
                : undefined
            }
          >
            <Body
              body={expanded ? message.body : hasHeadline ? message.headline : hasDetail ? summary : message.body}
              serif={human}
              mentionKind={mentionKind}
              oneLine={hasHeadline && !expanded}
            />
          </div>
          {/* A grouped row has no time to sit beside. */}
          {continued && message.edited_at && <div style={TIME_STYLE}>edited</div>}

          {long && (
            <button
              type="button"
              onClick={() => setExpanded(!expanded)}
              style={{ padding: '4px 0', fontSize: 12, fontWeight: 700, color: 'var(--accent)', cursor: 'pointer' }}
            >
              {hasHeadline ? (expanded ? 'Collapse' : 'Expand') : (expanded ? 'Show less' : 'Show more')}
            </button>
          )}

          {/* A reply reaches its thread, which this line does not describe. */}
          {human && place === undefined && !message.reply_to && (
            <div style={{ marginTop: 3, font: '400 11px var(--font-machine)', color: 'var(--text-meta)' }}>
              {deliveryLine(message.mentions, roomAgents)}
            </div>
          )}

          {actions}

          {onReact && (
            <Reactions
              reactions={message.reactions}
              me={myName}
              onToggle={(reaction, held) => onReact(message, reaction, held)}
            />
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * A run's start, drawn as what it is: a person asking for an agent. The run's
 * own events are not rows; the line under this one says where it has got to,
 * and links to the session when the agent it became says where that opens.
 */
function RunRow({ message, start, messages, roster, canHover, mentionKind, dot, timeOf }: {
  message: Message
  start: RunStep
  messages: Message[]
  roster: Agent[]
  canHover: boolean
  mentionKind: (handle: string) => MentionKind
  dot: ReactNode
  timeOf: (when: string) => string
}) {
  // The agent the run became. Its branch is null until the run reports one,
  // and no agent is named null.
  const branch = latestRunStep(messages, start).run.branch
  const link = openLink(roster.find((candidate) => candidate.name === branch), canHover)
  return (
    <div style={{ display: 'flex', gap: TEXT_COLUMN - FACE, padding: '8px 0 1px' }}>
      <Face name={message.sender} size={FACE} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 1 }}>
          <span style={PERSON_NAME_STYLE}>{message.sender}</span>
          <span style={TIME_STYLE}>{timeOf(message.posted_at)}</span>
          {dot}
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 7 }}>
          <span style={{ flex: 'none', font: '500 var(--size-body)/var(--line-body) var(--font-machine)', color: 'var(--accent)' }}>
            /agent
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Body body={start.meta} serif mentionKind={mentionKind} />
          </div>
        </div>
        <div style={{ marginTop: 3, font: '400 11px var(--font-machine)', color: 'var(--text-meta)' }}>
          {runLine(messages, start)}
          {link && (
            <>
              {' · '}
              <a href={link} {...newTabFor(link)} style={{ color: 'var(--text-soft)', textDecoration: 'underline', textUnderlineOffset: 2 }}>
                Open in Claude app
              </a>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export interface StreamProps {
  messages: Message[]
  /** What the reader has not read: each gets a dot, and a thread row counts its replies in it. */
  unread?: ReadonlySet<string>
  /** The message the New line goes before. */
  newLineBefore?: string | null
  roster: Agent[]
  /** The room being read, which an agent started from it is labelled with. */
  channelId: string
  /** The message j/k is sitting on, if any. */
  selectedId: string | null
  /** Shown above the first message: where history stops and why. */
  horizon?: string | null
  /** The room's page would not load, so an empty room says that rather than "nothing said". */
  loadFailed?: boolean
  /** Without it a name opens nothing. */
  onOpenProfile?: (name: string) => void
  /** Whoever is reading: their reaction chips are marked, and a message tagging them is washed. */
  myName: string
  /** Without it a message has no reactions, no toolbar and no long press. */
  onReact?: (message: Message, reaction: ReactionName, held: boolean) => void
  /**
   * Open the thread `message` is in: from the hover bar, the long press, a
   * thread's row, a reply's line in the room and its label in a conversation.
   * Inside a thread the reply box is the way to reply, and nothing offers it.
   */
  onOpenThread?: (message: Message) => void
  /** The thread open beside the room, whose first message is washed. */
  openThreadId?: string | null
  /** Draw this thread in place of the room: its first message, how many replied, then the replies. */
  threadId?: string
  /** For the phone's long-press sheet, which offers a direct message only where `ownsAgent`. */
  onMention?: (name: string) => void
  onOpenDirect?: (name: string) => void
  ownsAgent?: (name: string) => boolean
  /** Post `answer` to a question as a reply to it. */
  onAnswer: (message: Message, answer: string) => Promise<unknown>
  /**
   * A direct message's conversation rather than a room: each message says the
   * room it was said in, a change of place starts a new group, and there is no
   * delivery line.
   */
  conversation?: {
    placeOf: (message: Message) => string | null
    actionsOf: (message: Message) => ReactNode
    /** Drawn in place of the line saying where a message was said; null draws nothing there. */
    labelOf?: (message: Message) => ReactNode
  }
  /** No day dividers, and each time says its day unless it is today: for short runs of messages from many days. */
  dayInTime?: boolean
}

/** The id of a message's row, which a search hit scrolls to. */
export function rowId(messageId: string): string {
  return `message-${messageId}`
}

/** Marks the New line, which a room opens at. */
export const NEW_LINE_ATTRIBUTE = 'data-new-line'

const NOTHING_UNREAD: ReadonlySet<string> = new Set()

/** How long a finger rests on a message to open its sheet, and how far it may drift before it is a scroll. */
const LONG_PRESS_MILLISECONDS = 500
const PRESS_DRIFT = 10

export function Stream({
  messages, unread = NOTHING_UNREAD, newLineBefore = null, roster, channelId, selectedId, horizon, loadFailed,
  onOpenProfile, myName, onReact, onOpenThread, openThreadId, threadId, onMention, onOpenDirect, ownsAgent, onAnswer,
  conversation, dayInTime = false,
}: StreamProps) {
  // The phone bar's "yesterday 14:59", which here starts the line rather than ending a sentence.
  const timeOf = dayInTime
    ? (when: string) => sinceLabel(when).replace(/^./, (first) => first.toUpperCase())
    : clockOf
  // Every message drawn with a dot keeps it, faded once read, until the
  // stream goes, so a row never shifts as its dot leaves.
  const dotted = useRef(new Set<string>())
  for (const id of unread) dotted.current.add(id)
  const dotOf = (message: Message) =>
    dotted.current.has(message.id) && <UnreadDot unread={unread.has(message.id)} />
  // Touch has no hover to reveal the toolbar, so a long press opens a sheet in its place.
  const canHover = useCanHover()
  const [sheetId, setSheetId] = useState<string | null>(null)
  const press = useRef<{ timer: number; x: number; y: number } | null>(null)
  useEffect(() => () => { if (press.current) window.clearTimeout(press.current.timer) }, [])
  // The message a reply line was clicked through to, marked for two seconds.
  const [marked, setMarked] = useState<string | null>(null)
  useEffect(() => {
    if (!marked) return
    const timer = setTimeout(() => setMarked(null), 2000)
    return () => clearTimeout(timer)
  }, [marked])

  function jumpTo(id: string) {
    const row = document.getElementById(rowId(id))
    if (!row) return
    row.scrollIntoView({ block: 'center', behavior: 'smooth' })
    setMarked(id)
  }

  function endPress() {
    if (press.current) window.clearTimeout(press.current.timer)
    press.current = null
  }

  /** What makes a row open its sheet when held still for half a second. */
  function pressHandlers(message: Message) {
    return {
      onPointerDown: (event: PointerEvent) => {
        endPress()
        const timer = window.setTimeout(() => setSheetId(message.id), LONG_PRESS_MILLISECONDS)
        press.current = { timer, x: event.clientX, y: event.clientY }
      },
      onPointerMove: (event: PointerEvent) => {
        const start = press.current
        if (start && Math.hypot(event.clientX - start.x, event.clientY - start.y) > PRESS_DRIFT) endPress()
      },
      onPointerUp: endPress,
      onPointerCancel: endPress,
      // The press is the menu; the browser's own would cover the sheet.
      onContextMenu: (event: MouseEvent) => event.preventDefault(),
    }
  }

  if (!messages.length) {
    return (
      <div
        role={loadFailed ? 'alert' : undefined}
        style={{
          flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', textAlign: 'center', padding: '40px 20px', gap: 14,
        }}
      >
        <div style={{ width: 44, height: 44, borderRadius: 'var(--radius-face)', background: 'var(--panel-strong)', border: '1px solid var(--border-strong)' }} />
        {loadFailed ? (
          <>
            <p style={{ fontFamily: 'var(--font-human)', fontSize: 17, color: 'var(--text-dim)', maxWidth: 340 }}>
              Could not load this room right now.
            </p>
            <p style={{ fontSize: 12.5, lineHeight: 1.6, color: 'var(--text-faint)', maxWidth: 340 }}>
              It tries again on its own and fills in once stagepost answers.
              Your agents keep working in the meantime.
            </p>
          </>
        ) : (
          <>
            <p style={{ fontFamily: 'var(--font-human)', fontSize: 17, color: 'var(--text-dim)', maxWidth: 340 }}>
              Nothing has been said in this room.
            </p>
            <p style={{ fontSize: 12.5, lineHeight: 1.6, color: 'var(--text-faint)', maxWidth: 340 }}>
              The repo is enabled and linked. Agents appear here the first time one
              of them speaks — or you can start it yourself.
            </p>
          </>
        )}
      </div>
    )
  }

  const agentsByName = new Map(roster.map((agent) => [agent.name, agent]))
  const roomAgents = roster.filter((agent) => !agent.visiting_from).map((agent) => agent.name)

  // Who a handle turns out to be. Agents come from the roster. The people are
  // whoever has spoken in this room, which is the only human list the room
  // holds without asking the server for the organisation's members — and a
  // person who has never posted here is not someone an agent can usefully tag
  // anyway. `@here` is its own kind: it names every agent at once, which is a
  // different act from naming one and has to look like one.
  const agentNames = new Set(roster.map((agent) => agent.name.toLowerCase()))
  const humanNames = new Set(
    messages
      .filter((message) => message.sender_type === 'human')
      .map((message) => message.sender.toLowerCase()),
  )
  const mentionKind = (handle: string): MentionKind => {
    if (handle === 'here') return 'everyone'
    if (agentNames.has(handle)) return 'agent'
    return humanNames.has(handle) ? 'human' : 'unknown'
  }
  const threads = threadsOf(messages)
  const inRoom = !conversation && threadId === undefined
  const root = messages.find((message) => message.id === threadId)
  const built = root ? buildThread(root, threads.get(root.id) ?? [])
    : buildStream(inRoom ? roomMessagesOf(messages) : messages, newLineBefore)
  const rows = dayInTime ? built.filter((row) => row.kind !== 'day') : built
  // What this view shows of the person's messages; a direct message has no receipts.
  const onIt = conversation ? []
    : onItOf(root ? [root, ...(threads.get(root.id) ?? [])] : roomMessagesOf(messages), messages, roster)
  // Inside a thread the reply box is the way to reply.
  const replyInThreadOf = (message: Message) =>
    (onOpenThread && threadId === undefined ? () => onOpenThread(message) : undefined)
  const sheetMessage = messages.find((message) => message.id === sheetId)

  return (
    <>
      {horizon && (
        <p style={{ textAlign: 'center', padding: '10px 0 18px', margin: 0 }}>
          <span style={{ fontFamily: 'var(--font-human)', fontStyle: 'italic', fontSize: 13, color: 'var(--text-faint)' }}>
            {horizon}
          </span>
        </p>
      )}

      {rows.map((row, index) => {
        if (row.kind === 'day') {
          return (
            <div key={row.key} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '22px 2px 10px' }}>
              <span style={{ flex: 1, height: 1, background: 'var(--border)' }} />
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.18em', textTransform: 'uppercase', color: 'var(--text-dim)' }}>
                {row.label}
              </span>
              <span style={{ flex: 1, height: 1, background: 'var(--border)' }} />
            </div>
          )
        }
        if (row.kind === 'new') {
          return (
            <div key={row.key} {...{ [NEW_LINE_ATTRIBUTE]: 'yes' }} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 0 2px' }}>
              <span style={{ flex: 1, height: 1, background: 'var(--accent)' }} />
              <span style={{ font: '700 11.5px var(--font-interface)', color: 'var(--accent)' }}>New</span>
            </div>
          )
        }
        if (row.kind === 'replies') {
          return (
            <div key={row.key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 0 2px' }}>
              <span style={{ font: '400 12px var(--font-interface)', color: 'var(--text-meta)' }}>{row.label}</span>
              <span style={{ flex: 1, height: 1, background: 'var(--border)' }} />
            </div>
          )
        }

        const message = row.message!
        const selected = message.id === selectedId
        const run = message.event?.run
        const place = conversation?.placeOf(message)
        const actionable = message.sender_type !== 'event' && onReact
        const opensSheet = !canHover && actionable
        // Only a thread's first message carries its row, never a reply whose thread starts before the page.
        const replies = inRoom && !message.reply_to ? threads.get(message.id) : undefined
        // A reply in the room shows the thread it answers, or what it answers
        // when the page no longer holds the start of its thread.
        const replyTo = inRoom ? message.reply_to : null
        const answered = replyTo ? threadRootOf(messages, message) : null
        const answering = !replyTo ? null
          : answered ? onOpenThread && <ThreadLine headline={answered.headline || answered.body} onClick={() => onOpenThread(message)} />
          : message.reply_to_preview && (
            <ReplyLine
              preview={message.reply_to_preview}
              nickname={agentsByName.get(message.reply_to_preview.sender)?.nickname}
              onClick={messages.some((candidate) => candidate.id === replyTo) ? () => jumpTo(replyTo) : undefined}
            />
          )
        return (
          <div
            key={row.key}
            id={rowId(message.id)}
            data-message-id={message.id}
            {...(selected ? { [SELECTED_ATTRIBUTE]: 'yes' } : {})}
            {...(opensSheet ? pressHandlers(message) : {})}
            // An event is the room reporting state, so it gets no toolbar and
            // no lift.
            className={message.sender_type === 'event' ? undefined : 'sp-message'}
            style={{
              // A held finger selects text and opens the system's callout on
              // iOS; Copy text in the sheet stands in for both.
              ...(opensSheet ? { userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none' } : {}),
              animation: 'sp-in .28s ease-out both',
              margin: '0 -8px', padding: '0 8px', borderRadius: 'var(--radius-md)',
              transition: 'background-color .6s ease-out',
              backgroundColor: message.id === openThreadId || message.id === marked ? 'var(--accent-wash)'
                : selected ? 'rgba(255,255,255,.055)'
                  : tagsReader(message, myName) ? 'var(--mention-you-wash)' : undefined,
              boxShadow: selected ? 'inset 0 0 0 1px rgba(110,168,254,.4)' : undefined,
            }}
          >
            {message.event && run ? (
              <RunRow
                message={message}
                start={{ ...message.event, run }}
                messages={messages}
                roster={roster}
                canHover={canHover}
                mentionKind={mentionKind}
                dot={dotOf(message)}
                timeOf={timeOf}
              />
            ) : message.sender_type === 'event' ? (
              <EventRow message={message} dot={dotOf(message)} timeOf={timeOf} />
            ) : (
              <MessageRow
                message={message}
                agent={agentsByName.get(message.sender)}
                channelId={channelId}
                continued={(row.continued ?? false)
                  && (!conversation || conversation.placeOf(rows[index - 1].message!) === place)}
                roomAgents={roomAgents}
                mentionKind={mentionKind}
                myName={myName}
                onOpenProfile={onOpenProfile}
                onReact={onReact}
                answering={answering}
                onOpenThread={onOpenThread && (() => onOpenThread(message))}
                place={place}
                label={conversation?.labelOf?.(message)}
                dot={dotOf(message)}
                timeOf={timeOf}
                actions={<>
                  {message.options && (
                    <Answers options={message.options} answered={answerOf(message, messages)} onAnswer={(answer) => onAnswer(message, answer)} />
                  )}
                  {conversation?.actionsOf(message)}
                </>}
              />
            )}
            {replies && onOpenThread && (
              <ThreadRow
                replies={replies}
                unread={replies.filter((reply) => unread.has(reply.id)).length}
                onOpen={() => onOpenThread(message)}
              />
            )}
            {/* Rendered after the message, not before it. Floating, the order
                does not show; in the flow, where a touch device leaves it, a
                toolbar above the name reads as belonging to the message
                before this one. */}
            {actionable && canHover && (
              <MessageToolbar
                reactions={message.reactions}
                me={myName}
                onToggle={(reaction, held) => onReact(message, reaction, held)}
                onReply={replyInThreadOf(message)}
              />
            )}
          </div>
        )
      })}

      <OnItLine onIt={onIt} ownsAgent={ownsAgent} />

      {sheetMessage && onReact && (
        <MessageSheet
          message={sheetMessage}
          me={myName}
          onClose={() => setSheetId(null)}
          onToggle={(reaction, held) => onReact(sheetMessage, reaction, held)}
          onReply={replyInThreadOf(sheetMessage)}
          onMention={onMention && (() => onMention(sheetMessage.sender))}
          onOpenDirect={onOpenDirect && ownsAgent?.(sheetMessage.sender)
            ? () => onOpenDirect(sheetMessage.sender) : undefined}
        />
      )}
    </>
  )
}
